import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * Lightweight light/dark theme switch — controls ONLY the three
 * customer-facing pages we redesigned (BrandLanding, HomePage, CatalogPage).
 * Admin / staff surfaces are unaffected.
 *
 * • Persisted in localStorage under "jp_theme"
 * • Defaults to "dark" (the redesigned premium look)
 * • Exposed via `useTheme()` → { theme, setTheme, toggleTheme }
 */
const ThemeContext = createContext({
  theme: 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
});

const STORAGE_KEY = 'jp_theme';

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'light' || saved === 'dark' ? saved : 'dark';
    } catch {
      return 'dark';
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  const setTheme = (next) => {
    if (next === 'light' || next === 'dark') setThemeState(next);
  };
  const toggleTheme = () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
